import React, { useState } from 'react';
import './ProjectUserContainer.scss';

import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import { useTranslation } from 'react-i18next';
import ProjectHeader from '../../ProjectHeaderBar/ProjectHeader';
import { DefaultProject, Project, ProjectContext } from '../../../types/Project';
import ProjectMainSection from '../../ProjectMainSection/ProjectMainSection';
import ProjectControlPanel from '../../ProjectControlPanel/ProjectControlPanel';
import { useParams } from 'react-router-dom';
import { useGet } from 'restful-react';
import { OkResponse } from '../../../common/interfaces';
import useSignalRConnection from '../../../hooks/useSignalRConnection';
import { getSourceEncoder } from '../../../helpers/sourceEncoderHelper/sourceEncoderHelper';
import { AcceptTermsModal } from '../../Modals/AcceptTermsModal/AcceptTermsModal';
import useAutosaveProject from '../../../hooks/useAutosaveProject';
import ProjectDetailsProvider from '../../../contexts/project/ProjectProvider';
import { BroadcastedProjectModal } from '../../Modals/BroadcastedProjectModal/BroadcastedProjectModal';
import moment from 'moment';
import { UserTypeEnum } from '../../../types/enums/UserTypesEnum';

type RouterParams = {
  token: string;
};

type ProjectContainerProps = {
  userType: UserTypeEnum;
  hasAcceptedForTest?: boolean;
};

const ProjectContainer = ({ userType, hasAcceptedForTest }: ProjectContainerProps) => {
  const { t } = useTranslation();
  const context: ProjectContext = useAutosaveProject(DefaultProject);

  const [hasAccepted, setHasAccepted] = useState<boolean>(hasAcceptedForTest ?? false);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [hasBroadcasted, setHasBroadcasted] = useState<boolean>(false);
  const [hubCreated, setHubCreated] = useState<boolean>(false);
  const { token } = useParams<RouterParams>();
  const {
    createHubConnection,
    isChatConnected,
    chatMessages,
    sendMessageToRoom,
    sendRequestControl,
    sendTakeControl,
    sendGiveControl,
    getPeerInfo,
    peerRequestingControl,
    connectedPeers,
    sendProjectToAllUsers,
    isAwaitingApiSaveToComplete,
    isUserActive,
    isUserLive,
    myConnectionIsLive,
  } = useSignalRConnection(context);

  const { data: project, loading, error } = useGet({
    path: '/project-users/' + token,
    resolve: (project: OkResponse<Project>) => {
      setHasFetched(true);
      const result = project.result;

      if (
        result.broadcastEndedUtc ||
        (moment(result.scheduledDateTimeUtc).add(result.duration, 'minutes') < moment.utc() &&
          !result.broadcastStartedUtc)
      ) {
        setHasBroadcasted(true);
        return;
      }

      const encoder = getSourceEncoder(result?.stream);

      if (encoder) {
        result.encoder = encoder;
      }

      context.setProject(result);

      if (!hubCreated) {
        createHubConnection(
          result?.id ?? '',
          result?.projectUserId ?? '',
          token,
          userType,
          result?.projectUserName ?? '',
          result?.messages ?? [],
        );
        setHubCreated(true);
      }

      return result;
    },
  });

  if (hasBroadcasted && hasFetched) {
    return <BroadcastedProjectModal />;
  }

  if (!hasAccepted && hasFetched) {
    return (
      <AcceptTermsModal
        userType={userType}
        isOpen={true}
        handleCloseModal={() => setHasAccepted(true)}
      />
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={t('error-messages.generic-error')} />;
  }

  if (!project) {
    return null;
  }

  return (
    <ProjectDetailsProvider value={context}>
      <section>
        <ProjectHeader
          isProducer={false}
          reloadProject={() => {
            return null;
          }}
        />
        <div className="project-container">
          <ProjectControlPanel
            userType={userType}
            chatMessages={chatMessages}
            token={token}
            sendMessageToRoomCallback={sendMessageToRoom}
            isChatConnected={isChatConnected}
            sendProjectToAllUsers={sendProjectToAllUsers}
          />
          <ProjectMainSection
            isProducer={false}
            token={token}
            sendRequestControlCallback={sendRequestControl}
            sendTakeControlCallback={sendTakeControl}
            sendGiveControlCallback={sendGiveControl}
            getPeerInfoCallback={getPeerInfo}
            peerRequestingControl={peerRequestingControl}
            connectedPeers={connectedPeers}
            isAwaitingApiSaveToComplete={isAwaitingApiSaveToComplete}
            isUserActiveCallback={isUserActive}
            isUserLiveCallback={isUserLive}
            myConnectionIsLive={myConnectionIsLive}
          />
        </div>
      </section>
    </ProjectDetailsProvider>
  );
};

export default ProjectContainer;
